
























































































































import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';

import { BaseVue } from '@/BaseVue';
import CreateInventoryView from '@/components/inventory/CreateInventoryView.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiDataTable from '@/components/ui/UiDataTable.vue';
import UiTruncateText from '@/components/ui/UiTruncateText.vue';
import WalletListNew from '@/components/wallets/WalletListNew.vue';

import { baConfig } from '../../../../config';
import { ApiSvcInventoryView, ApiSvcInventoryViewUpdate, InventoryApi } from '../../../../generated/api-svc';

type UpdatesHeader = { id: string; label: string; defaultVisibility: boolean };

@Component({
  components: {
    CreateInventoryView,
    UiButton,
    WalletListNew,
    UiDataTable,
    UiTruncateText,
  },
})
export default class InventoryViewUpdates extends BaseVue {
  @Prop({ default: null })
  public readonly view!: ApiSvcInventoryView | null;

  public updates: ApiSvcInventoryViewUpdate[] = [];

  public baseHeaders = [
    {
      id: 'id',
      label: 'ID',
      defaultVisibility: true,
    },
    {
      id: 'runId',
      label: 'Run ID',
      defaultVisibility: true,
    },
    {
      id: 'started-by',
      label: 'Started By',
      defaultVisibility: true,
    },
    {
      id: 'started',
      label: 'Started',
      defaultVisibility: true,
    },
    {
      id: 'completed',
      label: 'Completed',
      defaultVisibility: true,
    },
    {
      id: 'status',
      label: 'Status',
      defaultVisibility: true,
    },
  ];

  public lots: string[] = [];
  public isLoading = false;

  parseStatus(item: ApiSvcInventoryViewUpdate) {
    if (item.status !== 'Errored') return item.status;
    const firstError = item.errors?.[0];
    return firstError ? `Errored: ${firstError}` : 'Errored';
  }

  public async mounted() {
    await this.loadUpdates();
  }

  // Resolves the "Started By" label, falling back to the org user map (by userId, from the
  // orgUsers store module) and then email/userId when the update's stored name is missing.
  public startedByLabel(update: ApiSvcInventoryViewUpdate): string {
    const requestedBy = update.updateRequestedBy;
    if (!requestedBy) return '';
    return (
      requestedBy.name ||
      this.$store.getters['orgUsers/orgUserName'](requestedBy.userId) ||
      requestedBy.email ||
      requestedBy.userId ||
      ''
    );
  }

  public shouldShowCancelButton(update: ApiSvcInventoryViewUpdate) {
    return update.status === 'Running' || update.status === 'New' || update.status === 'Queued';
  }

  // Reference run columns are shown by default; users can hide them from the columns dropdown.
  // The table resets column choices whenever its headers change, so only rebuild them when the Actions column
  // comes or goes, not on every refresh of the updates list.
  public computedHeaders: UpdatesHeader[] = [];

  // Built here rather than in the field initializer: baseHeaders isn't set yet while class fields are collected.
  public created() {
    this.computedHeaders = this.buildHeaders(this.shouldShowActionsColumn());
  }

  @Watch('updates')
  public onUpdatesChanged() {
    const showActions = this.shouldShowActionsColumn();
    if (this.computedHeaders.some((h) => h.id === 'actions') !== showActions) {
      this.computedHeaders = this.buildHeaders(showActions);
    }
  }

  private buildHeaders(showActions: boolean): UpdatesHeader[] {
    const headers = [
      ...this.baseHeaders,
      { id: 'runIdReference', label: 'Reference Run ID', defaultVisibility: true },
      { id: 'startingSEC', label: 'Reference Run End Date', defaultVisibility: true },
      { id: 'endingSEC', label: 'Run End Date', defaultVisibility: true },
    ];
    return showActions ? [...headers, { id: 'actions', label: 'Actions', defaultVisibility: true }] : headers;
  }

  public shouldShowActionsColumn() {
    return this.updates.some(
      (update) => update.status === 'Running' || update.status === 'New' || update.status === 'Queued'
    );
  }

  public async loadUpdates() {
    if (this.view?.id) {
      this.isLoading = true;
      try {
        const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
        const orgId = this.$store.state.currentOrg.id;
        const resp = await svc.getViewUpdates(orgId, this.view?.id, { withCredentials: true });
        if (resp.status === 200) {
          this.updates = resp.data.items;
          // Only resolve names via the orgUsers store (core-svc) when a row is actually missing
          // one (e.g. SSO users). Fire in the background so it never blocks the table render; the
          // store memoizes per org so this is a no-op after the first load.
          if (this.updates.some((u) => u.updateRequestedBy && !u.updateRequestedBy.name)) {
            this.$store.dispatch('orgUsers/loadOrgUsers');
          }
        }
      } finally {
        this.isLoading = false;
      }
    }
  }

  public async cancelViewUpdate(updateId: string) {
    if (this.view?.id) {
      this.isLoading = true;
      try {
        const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
        const orgId = this.$store.state.currentOrg.id;
        const resp = await svc.cancelViewUpdate(orgId, this.view?.id, updateId, { withCredentials: true });
        if (resp.status === 200) {
          if (!resp.data.success) this.showErrorSnackbar('Failed to cancel view');
          await this.loadUpdates();
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
}
